import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { SuperendividamentoResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const Superendividamento = () => {
  const [rendaLiquida, setRendaLiquida] = useState("");
  const [gastosMensais, setGastosMensais] = useState("");
  const [resultado, setResultado] = useState<SuperendividamentoResult | null>(null);
  
  const { calcularSuperendividamento, loading } = useCalculos();

  useCalculoPrefill("superendividamento", (v) => {
    setRendaLiquida(v.rendaLiquida ?? "");
    setGastosMensais(v.gastosMensais ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!rendaLiquida || !gastosMensais) {
      return;
    }

    try {
      const result = await calcularSuperendividamento({
        rendaLiquida: parseFloat(rendaLiquida),
        gastosMensais: parseFloat(gastosMensais)
      });
      setResultado(result);
      addToHistory({
        type: "superendividamento",
        label: "Superendividamento",
        values: { rendaLiquida, gastosMensais },
        summary: `Disponível ${formatCurrency(result.capacidadePagamento)} • Parcela máx ${formatCurrency(result.parcelaMaxima)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Superendividamento</CardTitle>
        <p className="text-sm text-muted-foreground">
          Análise da capacidade de pagamento (máximo 30% da renda disponível)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="renda-liquida">Renda Líquida Mensal</Label>
            <Input
              id="renda-liquida"
              type="number"
              placeholder="3000.00"
              value={rendaLiquida}
              onChange={(e) => setRendaLiquida(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="gastos-mensais">Gastos Essenciais Mensais</Label>
            <Input
              id="gastos-mensais"
              type="number"
              placeholder="2000.00"
              value={gastosMensais}
              onChange={(e) => setGastosMensais(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Alimentação, moradia, transporte, saúde
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Capacidade"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Análise de Capacidade:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Renda Disponível:</strong> {formatCurrency(resultado.capacidadePagamento)}</p>
                <p><strong>Parcela Máxima (30%):</strong> {formatCurrency(resultado.parcelaMaxima)}</p>
              </div>
              <div>
                {resultado.capacidadePagamento > 0 ? (
                  <div className="text-green-600">
                    <p><strong>✓ Situação:</strong> Viável</p>
                    <p className="text-xs">Possui capacidade de pagamento</p>
                  </div>
                ) : (
                  <div className="text-red-600">
                    <p><strong>⚠ Situação:</strong> Superendividado</p>
                    <p className="text-xs">Gastos excedem a renda</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};